
import { db } from '../firebase';
import {
    doc,
    onSnapshot,
    updateDoc,
    arrayUnion,
    arrayRemove,
    query,
    collection,
    where,
    deleteDoc,
    getDoc,
    getDocs,
    writeBatch,
    serverTimestamp,
    addDoc
} from 'firebase/firestore';
import { Team, Invitation, UserProfile, TeamMembership } from '../types';
import {getFunctions, httpsCallable} from "firebase/functions";

export const TeamService = {
    /**
     * Subscribes to a team document.
     */
    listenToTeam(teamId: string, callback: (team: Team | null) => void) {
        return onSnapshot(doc(db, 'teams', teamId), (snap) => {
            if (snap.exists()) {
                callback({ id: snap.id, ...snap.data() } as Team);
            } else {
                callback(null);
            }
        });
    },

    /**
     * Subscribes to pending invitations for a team.
     */
    listenToPendingInvitations(teamId: string, callback: (invitations: Invitation[]) => void) {
        const q = query(
            collection(db, 'invitations'), 
            where('teamId', '==', teamId),
            where('status', '==', 'pending')
        );
        return onSnapshot(q, (snap) => {
            callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as Invitation)));
        });
    },

    /**
     * Subscribes to pending email invitations for a team.
     */
    listenToPendingEmailInvitations(teamId: string, callback: (invitations: any[]) => void) {
        const q = query(
            collection(db, 'emailInvitations'),
            where('teamId', '==', teamId),
            where('status', '==', 'pending')
        );
        return onSnapshot(q, (snap) => {
            callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
    },

    /**
     * Subscribes to all teams within an organization.
     * MOVED FROM StandUpContext.tsx (My Organization's Teams Listener)
     */
    listenToAllOrgTeams(orgId: string, callback: (teams: Team[]) => void) {
        const q = query(collection(db, 'teams'), where('orgId', '==', orgId));
        return onSnapshot(q, (snap) => {
            callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as Team)));
        });
    },

    /**
     * Approves a join request for a team.
     */
    async approveJoinRequest(teamId: string, teamName: string, userId: string) {
        const teamRef = doc(db, 'teams', teamId);
        await updateDoc(teamRef, {
            pendingRequests: arrayRemove(userId),
            members: arrayUnion(userId)
        });

        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            teams: arrayUnion({ teamId, teamName, role: 'engineer' }),
            teamIds: arrayUnion(teamId)
        });
    },

    /**
     * Declines a join request for a team.
     */
    async declineJoinRequest(teamId: string, userId: string) {
        const teamRef = doc(db, 'teams', teamId);
        await updateDoc(teamRef, {
            pendingRequests: arrayRemove(userId)
        });
    },

    /**
     * Cancels an email invitation.
     */
    async cancelEmailInvitation(inviteId: string) {
        await deleteDoc(doc(db, 'emailInvitations', inviteId));
    },

    /**
     * Fetches a single team document by ID.
     */
    async getTeam(teamId: string): Promise<Team | null> {
        const snap = await getDoc(doc(db, 'teams', teamId));
        if (snap.exists()) {
            return { id: snap.id, ...snap.data() } as Team;
        }
        return null;
    },

    /**
     * Updates a team document.
     */
    async updateTeam(teamId: string, data: Partial<Team>) {
        const teamRef = doc(db, 'teams', teamId);
        await updateDoc(teamRef, {
            ...data,
            updatedAt: Date.now()
        });
    },

    /**
     * Checks if a team name is already taken within an organization.
     */
    async isTeamNameDuplicate(orgId: string, name: string): Promise<boolean> {
        const q = query(
            collection(db, 'teams'),
            where('orgId', '==', orgId),
            where('name', '==', name)
        );
        const snap = await getDocs(q);
        return !snap.empty;
    },

    /**
     * Atomically updates team project mappings for a specific provider.
     * Uses writeBatch to handle deletions and insertions.
     */
    async updateTeamProjectMappings(
        teamId: string, 
        provider: string, 
        projectIds: string[], 
        availableProjects: {id: string, name: string}[],
        orgId?: string
    ) {
        const teamRef = doc(db, 'teams', teamId);
        const teamSnap = await getDoc(teamRef);
        const oldIds: string[] = teamSnap.data()?.externalProjectIds?.[provider] || [];
        const removedIds = oldIds.filter(id => !projectIds.includes(id));

        // 1. Update the team document
        await updateDoc(teamRef, {
            [`externalProjectIds.${provider}`]: projectIds
        });

        // 2. Perform batch updates for mappings
        const batch = writeBatch(db);
        
        // Remove old mappings
        removedIds.forEach(id => {
            batch.delete(doc(db, 'projectTeamMap', `${provider}_${id}_${teamId}`));
        });

        // Add/Update new mappings
        projectIds.forEach(projectId => {
            const mapRef = doc(db, 'projectTeamMap', `${provider}_${projectId}_${teamId}`);
            const projectObj = availableProjects.find(p => p.id === projectId);
            const projectName = projectObj ? projectObj.name : projectId;

            batch.set(mapRef, {
                standUpTeamId: teamId,
                orgId: orgId || null,
                provider: provider,
                projectId: projectId,
                projectName: projectName,
                updatedAt: Date.now()
            });
        });

        await batch.commit();
    },

    /**
     * Fetches all teams in the organization that the user is NOT in and are not private.
     */
    async fetchDiscoverableTeams(orgId: string, myTeamIds: string[]): Promise<Team[]> {
        const q = query(
            collection(db, 'teams'),
            where('orgId', '==', orgId),
            where('visibility', '!=', 'private')
        );
        const snap = await getDocs(q);
        const teams = snap.docs.map(d => ({ id: d.id, ...d.data() } as Team));
        
        return teams.filter(t => !myTeamIds.includes(t.id));
    },

    /**
     * Requests access to a protected team.
     */
    async requestTeamAccess(teamId: string, userId: string) {
        const teamRef = doc(db, 'teams', teamId);
        await updateDoc(teamRef, {
            pendingRequests: arrayUnion(userId)
        });
    },

    /**
     * Creates a new team document and adds it to the owner's profile.
     * Character-for-character match from useTeamManagement.ts
     */
    async createTeam(orgId: string, name: string, ownerId: string, visibility: 'public' | 'protected' | 'private') {
        const teamRef = await addDoc(collection(db, 'teams'), {
            name,
            orgId,
            ownerId,
            visibility,
            members: [ownerId],
            pendingRequests: [],
            createdAt: serverTimestamp()
        });

        const newMembership: TeamMembership = {
            teamId: teamRef.id,
            teamName: name,
            role: 'manager'
        };

        const userRef = doc(db, 'users', ownerId);
        await updateDoc(userRef, {
            teams: arrayUnion(newMembership),
            teamIds: arrayUnion(teamRef.id),
            activeTeamIds: [teamRef.id],
            activeTeamId: teamRef.id
        });

        return teamRef;
    },

    /**
     * Joins a team and updates user profile.
     */
    async joinTeam(teamId: string, teamName: string, userId: string) {
        const newMembership: TeamMembership = {
            teamId,
            teamName,
            role: 'engineer'
        };

        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            teams: arrayUnion(newMembership),
            teamIds: arrayUnion(teamId),
            activeTeamIds: [teamId],
            activeTeamId: teamId
        });
    },

    /**
     * Sends an in-app invitation.
     */
    async sendInvitation(orgId: string, teamId: string, teamName: string, senderId: string, senderName: string, inviteeId: string) {
        await addDoc(collection(db, 'invitations'), {
            teamId,
            teamName,
            orgId,
            inviteeId,
            inviterId: senderId,
            inviterName: senderName,
            status: 'pending',
            createdAt: Date.now()
        });
    },

    /**
     * Checks if a pending invitation already exists for a user and team.
     */
    async isInvitationDuplicate(orgId: string, teamId: string, inviteeId: string): Promise<boolean> {
        const q = query(
            collection(db, 'invitations'),
            where('orgId', '==', orgId),
            where('teamId', '==', teamId),
            where('inviteeId', '==', inviteeId),
            where('status', '==', 'pending')
        );
        const existing = await getDocs(q);
        return !existing.empty;
    },

    /**
     * Sends an email invitation via Cloud Function.
     */
    async inviteByEmail(email: string, teamId: string, teamName: string) {
        const inviteFunc = httpsCallable(getFunctions(), 'inviteUserByEmail');
        await inviteFunc({ email, teamId, teamName });
    },

    /**
     * Accepts an invitation (Standard UID or Email).
     */
    async acceptInvitation(userId: string, invitation: Invitation) {
        // --- EMAIL INVITE HANDLER ---
        if (!invitation.inviteeId) {
            console.log(`[Invite] Accepting EMAIL-based invitation ${invitation.id} via Cloud Function`);
            const acceptEmailInviteFunc = httpsCallable(getFunctions(), 'acceptEmailInvite');
            try {
                await acceptEmailInviteFunc({ token: invitation.id });
                console.log(`[Invite] Successfully accepted email invitation`);
                return;
            } catch (e) {
                console.error(`[Invite Error] Cloud Function accept failed:`, e);
                throw e;
            }
        }

        // --- STANDARD UID INVITE HANDLER ---
        console.log(`[Invite] Accepting standard invitation for team ${invitation.teamId}`);
        const batch = writeBatch(db);
        const userRef = doc(db, 'users', userId);
        const inviteRef = doc(db, 'invitations', invitation.id);

        const newMembership: TeamMembership = {
            teamId: invitation.teamId,
            teamName: invitation.teamName,
            role: 'engineer'
        };

        batch.update(userRef, {
            teams: arrayUnion(newMembership),
            teamIds: arrayUnion(invitation.teamId),
            activeTeamIds: arrayUnion(invitation.teamId),
            orgIds: arrayUnion(invitation.orgId),
            orgId: invitation.orgId
        });

        batch.update(inviteRef, { status: 'accepted' });
        await batch.commit();
        console.log(`[Invite] Successfully accepted invitation`);
    },

    /**
     * Declines an invitation.
     */
    async declineInvitation(invitationId: string) {
        console.log(`[Invite] Declining/Cancelling invitation: ${invitationId}`);
        try {
            const inviteRef = doc(db, 'invitations', invitationId);
            await updateDoc(inviteRef, { status: 'declined' });
            console.log(`[Invite] Successfully declined invitation`);
        } catch (e) {
            console.error(`[Invite Error] Decline failed:`, e);
            throw e;
        }
    },

    /**
     * Leaves a team and unassigns tasks.
     */
    async leaveTeam(teamId: string, userId: string, membership: TeamMembership, effectiveTeamIds: string[], allUserTeams: TeamMembership[]) {
        const batch = writeBatch(db);
        const userRef = doc(db, 'users', userId);

        const assignedTasksQuery = query(
            collection(db, 'tasks'),
            where('teamId', '==', teamId),
            where('assigneeId', '==', userId),
            where('status', 'in', ['todo', 'in-progress', 'deep-work', 'blocked'])
        );

        const tasksSnap = await getDocs(assignedTasksQuery);
        tasksSnap.docs.forEach(taskDoc => {
            batch.update(taskDoc.ref, { assigneeId: '', updatedAt: serverTimestamp() });
        });

        const otherTeams = allUserTeams.filter(t => t.teamId !== teamId);
        const nextTeamId = otherTeams.length > 0 ? otherTeams[0].teamId : null;
        const newActiveIds = effectiveTeamIds.filter(id => id !== teamId);
        let finalActiveIds = newActiveIds;
        if (finalActiveIds.length === 0 && nextTeamId) finalActiveIds = [nextTeamId];

        batch.update(userRef, {
            teams: arrayRemove(membership),
            teamIds: arrayRemove(teamId),
            activeTeamId: nextTeamId,
            activeTeamIds: finalActiveIds
        });

        await batch.commit();
    },

    /**
     * Deletes a team and all its data.
     */
    async deleteTeam(teamId: string) {
        const batch = writeBatch(db);
        
        // 1. Delete tasks
        const taskDocs = await getDocs(query(collection(db, 'tasks'), where('teamId', '==', teamId)));
        taskDocs.forEach(d => batch.delete(d.ref));

        // 2. Remove from member profiles
        const membersSnap = await getDocs(query(collection(db, 'users'), where('teamIds', 'array-contains', teamId)));
        membersSnap.docs.forEach(memberDoc => {
            const memberData = memberDoc.data() as UserProfile;
            const membershipToRemove = memberData.teams?.find(t => t.teamId === teamId);
            if (membershipToRemove) {
                const updates: any = {
                    teams: arrayRemove(membershipToRemove),
                    teamIds: arrayRemove(teamId),
                    activeTeamIds: arrayRemove(teamId)
                };
                if (memberData.activeTeamId === teamId) {
                    const remainingTeams = memberData.teams?.filter(t => t.teamId !== teamId) || [];
                    updates.activeTeamId = remainingTeams.length > 0 ? remainingTeams[0].teamId : null;
                }
                batch.update(memberDoc.ref, updates);
            }
        });

        // 3. Delete projectTeamMap entries
        const mappingSnap = await getDocs(query(collection(db, 'projectTeamMap'), where('standUpTeamId', '==', teamId)));
        mappingSnap.forEach(d => batch.delete(d.ref));

        // 4. Delete the team document
        batch.delete(doc(db, 'teams', teamId));

        await batch.commit();
    },

    /**
     * Updates a user's role within a team.
     */
    async updateMemberRole(userId: string, teamId: string, newRole: 'manager' | 'engineer') {
        const userRef = doc(db, 'users', userId);
        const snapshot = await getDoc(userRef);
        if (!snapshot.exists()) return;
        const data = snapshot.data() as UserProfile;
        if (!data.teams) return;
        const updatedTeams = data.teams.map(t => t.teamId === teamId ? { ...t, role: newRole } : t);
        await updateDoc(userRef, { teams: updatedTeams });
    },

    /**
     * Removes a member from a team and unassigns tasks.
     */
    async removeMember(userId: string, teamId: string, membershipToRemove: TeamMembership) {
        const userRef = doc(db, 'users', userId);
        const batch = writeBatch(db);

        const assignedTasksQuery = query(
            collection(db, 'tasks'),
            where('teamId', '==', teamId),
            where('assigneeId', '==', userId)
        );
        const tasksSnap = await getDocs(assignedTasksQuery);
        tasksSnap.docs.forEach(taskDoc => {
            batch.update(taskDoc.ref, { assigneeId: '', updatedAt: serverTimestamp() });
        });

        batch.update(userRef, {
            teams: arrayRemove(membershipToRemove),
            teamIds: arrayRemove(teamId),
            activeTeamIds: arrayRemove(teamId)
        });
        await batch.commit();
    },

    /**
     * Fetches full user profiles for all members of a team.
     * Character-for-character match from original useTeamManagement.ts
     */
    async fetchTeamMembers(teamId: string): Promise<UserProfile[]> {
        const q = query(collection(db, 'users'), where('teamIds', 'array-contains', teamId));
        const snap = await getDocs(q);
        return snap.docs.map(d => ({ id: d.id, ...d.data() } as UserProfile));
    }
};
